import { BLOCKS, INLINES } from "@contentful/rich-text-types"
import { renderRichText as renderContentfulRichText } from "gatsby-source-contentful/rich-text"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import {
  RenderMark,
  RenderNode,
  RenderText,
  NodeRenderer
} from "@contentful/rich-text-react-renderer"
import { Typography } from "@eventbrite/marmalade"
import { ContentfulLivePreview } from "@contentful/live-preview"

// components
import { Alert } from "../../components/Alert/Alert"
import { VimeoVideo } from "../../components/VimeoVideo/VimeoVideo"
import { UniversalLink } from "../../components/UniversalLink/UniversalLink"

// styles
import * as styles from "./GatsbyImage.module.scss"

export interface RenderRichTextOptions {
  renderNode?: RenderNode
  renderMark?: RenderMark
  renderText?: RenderText
  references?: any
}

export interface VideoContent {
  __typename: string
  contentful_id: string
  videoTitle: string
  videoUrl: string
  videoThumbnailUrl: string
  videoCaption: {
    videoCaption: string
  }
  videoThumbnailAltText: {
    videoThumbnailAltText: string
  }
  sys: {
    type: string
    id: string
  }
}

export type HelpArticleAlertData = Pick<
  Queries.ContentfulAlert,
  | "contentful_id"
  | "alertShowIcon"
  | "alertDescription"
  | "alertDismissBoolean"
  | "alertTypeReference"
  | "alertCta"
  | "alertHeader"
> & {
  __typename: string
}

const embeddedEntryRenderer: NodeRenderer = (node) => {
  const entryId = node?.data?.target?.contentful_id

  if (node?.data?.target?.__typename === "ContentfulAlert") {
    const alertData = node.data.target as HelpArticleAlertData

    return (
      <Alert
        alertData={alertData}
        {...ContentfulLivePreview.getProps({ entryId, fieldId: "alertDescription" })}
      />
    )
  }

  if (node?.data?.target?.__typename === "ContentfulVideo") {
    const target = node?.data?.target as VideoContent

    return (
      <VimeoVideo
        src={target.videoUrl}
        caption={target?.videoCaption?.videoCaption}
        {...ContentfulLivePreview.getProps({ entryId, fieldId: "videoUrl" })}
      />
    )
  }

  if (node?.data?.target?.__typename === "ContentfulImage") {
    const image = node?.data?.target?.image

    if (image) {
      const { description, mimeType, width, height, url } = image
      // SVG
      if (mimeType === "image/svg+xml" && url) {
        const svgProps = width && height ? { width, height } : {}

        return (
          <img
            src={url}
            alt={description || ""}
            style={{ maxWidth: "100%" }}
            {...svgProps}
            {...ContentfulLivePreview.getProps({ entryId, fieldId: "image" })}
          />
        )
      }

      if (image?.gatsbyImageData) {
        // RASTER IMAGE
        const gatsbyImage = getImage(image.gatsbyImageData)
        if (gatsbyImage) {
          return (
            <GatsbyImage
              className={styles?.gatsbyImageWrapper}
              image={gatsbyImage}
              alt={image?.description}
              {...ContentfulLivePreview.getProps({
                entryId,
                fieldId: "image"
              })}
            />
          )
        }
      }
    }
  }

  return null
}

// This code block allows us to specially handle different parts of the rich text editor.
export const contentfulRenderOptions: RenderRichTextOptions = {
  renderNode: {
    // Handle hyperlinks with UniversalLink
    [INLINES.HYPERLINK]: (node, children) => {
      const uri = node.data?.uri as string
      
      if (!uri) {
        return <span>{children}</span>
      }

      return (
        <UniversalLink to={uri}>
          {children}
        </UniversalLink>
      )
    },
    [INLINES.EMBEDDED_ENTRY]: embeddedEntryRenderer,
    // Inside of {ContentfulHelpArticle.slug}.tsx there is this code: `export const query = graphql` this query fetches any gatsbyImageData that is available for an asset (if it is an image) and includes it.
    // We can handle all other Contentful Asset types here
    [BLOCKS.EMBEDDED_ENTRY]: embeddedEntryRenderer,
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      const target = node?.data?.target || {}
      const { gatsbyImageData, description, mimeType, width, height, url } =
        target

      // RASTER IMAGE
      if (gatsbyImageData) {
        const gatsbyImage = getImage(gatsbyImageData)
        if (gatsbyImage) {
          return (
            <GatsbyImage
              className={styles?.gatsbyImageWrapper}
              image={gatsbyImage}
              alt={description}
            />
          )
        }
      }
      if (node?.data?.target?.__typename === "ContentfulImage") {
        const gatsbyImageData = node?.data?.target?.image?.gatsbyImageData
        const description = node?.data?.target?.image?.description
        const gatsbyImage = gatsbyImageData ? getImage(gatsbyImageData) : null

        if (gatsbyImage) {
          return (
            <GatsbyImage
              className={styles?.gatsbyImageWrapper}
              image={gatsbyImage}
              alt={description}
            />
          )
        }
      }

      // SVG
      if (mimeType === "image/svg+xml" && url) {
        const svgProps = width && height ? { width, height } : {}

        return (
          <img
            src={url}
            alt={description || ""}
            style={{ maxWidth: "100%" }}
            {...svgProps}
          />
        )
      }

      return null
    },
    [BLOCKS.PARAGRAPH]: (node, children) => {
      // @TODO: need to remove this entirely
      return (
        <Typography variant="body-md" as="p">
          {children}
        </Typography>
      )
    }
  },
  references: []
}

export const renderRichText = (
  raw?: string,
  options: RenderRichTextOptions = {},
  debugLog = false
): React.ReactNode => {
  if (!raw) {
    return null
  }

  const finalOptions = { ...contentfulRenderOptions, ...options }

  if (debugLog) {
    console.log("🚀 ~ DEBUG LOG:", { raw, options, finalOptions })
  }

  return renderContentfulRichText(
    {
      raw: raw,
      references: options.references ? options.references : []
    },
    finalOptions
  )
}

export const customRendererToRemovePTag: RenderRichTextOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => <>{children}</>
  }
}
